(() => {
  const COMPONENT_NAME = "market-picker-popup";
  const SUGGESTIONS_URL =
    "/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true";
  const FLAG_CDN_BASE = "https://cdn.shopify.com/static/images/flags";
  const FLAG_WIDTH = 22;
  const CLOSE_ANIMATION_MS = 300;

  if (customElements.get(COMPONENT_NAME)) return;

  class MarketPickerPopup extends HTMLElement {
    #abortController = null;
    #dialog = null;
    #form = null;
    #countrySelect = null;
    #localeInput = null;
    #flagContainer = null;
    #title = null;
    #isClosing = false;
    #recommendedCountry = "";
    #recommendedLanguage = "";
    #detectedLocationName = "";

    connectedCallback() {
      this.#dialog = this.querySelector("[data-market-picker-dialog]");
      this.#form = this.querySelector("form");
      this.#countrySelect = this.querySelector(
        "[data-market-picker-country-select]",
      );
      this.#localeInput = this.querySelector(
        "[data-market-picker-locale-input]",
      );
      this.#flagContainer = this.querySelector("[data-market-picker-flag]");
      this.#title = this.querySelector("[data-market-picker-title]");

      if (!this.#dialog || !this.#countrySelect) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.#dialog.addEventListener("click", this.#handleDialogClick, {
        signal,
      });
      this.#dialog.addEventListener("close", this.#handleDialogClose, {
        signal,
      });
      this.#dialog.addEventListener("cancel", this.#handleDialogCancel, {
        signal,
      });
      this.#countrySelect.addEventListener(
        "change",
        this.#handleCountryChange,
        {
          signal,
        },
      );

      for (const dismissTrigger of this.querySelectorAll(
        "[data-market-picker-dismiss]",
      )) {
        dismissTrigger.addEventListener("click", this.#handleDismiss, {
          signal,
        });
      }

      this.#form?.addEventListener("submit", this.#handleFormSubmit, {
        signal,
      });

      this.#init();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
    }

    async #init() {
      if (this.dataset.forceShow === "true") {
        if (this.isConnected) {
          this.#open();
        }
        return;
      }

      const countryOverride = this.#countryOverride();

      if (!countryOverride && this.#isDismissed()) return;

      try {
        const shouldShow = await this.#shouldShowPopup(countryOverride);
        if (shouldShow && this.isConnected) {
          this.#open();
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
        /* Fail gracefully — do not show a broken popup */
      }
    }

    #countryOverride() {
      const override = new URLSearchParams(window.location.search)
        .get("detect_country")
        ?.trim();

      return override ? override.toUpperCase() : "";
    }

    #isDismissed() {
      const storageKey = this.dataset.storageKey;
      if (!storageKey) return false;

      const storage = this.#getStorage();
      return storage?.getItem(storageKey) === "true";
    }

    #getStorage() {
      return this.dataset.storageMode === "local"
        ? window.localStorage
        : window.sessionStorage;
    }

    #setDismissed() {
      const storageKey = this.dataset.storageKey;
      const storage = this.#getStorage();

      if (!storageKey || !storage) return;

      try {
        storage.setItem(storageKey, "true");
      } catch {
        /* Storage may be unavailable in private browsing */
      }
    }

    async #shouldShowPopup(countryOverride = "") {
      let detectedCountry = countryOverride;
      let detectedLocationName = "";
      let recommendedLanguage = this.#localeInput?.value ?? "";

      if (!detectedCountry) {
        const response = await fetch(SUGGESTIONS_URL, {
          headers: { Accept: "application/json" },
          signal: this.#abortController?.signal,
        });

        if (!response.ok || !this.isConnected) return false;

        const data = await response.json();
        detectedCountry = (
          data?.detected_values?.country?.handle ?? ""
        ).toUpperCase();
        detectedLocationName = (
          data?.detected_values?.country?.name ?? ""
        ).trim();
        recommendedLanguage =
          data?.suggestions?.[0]?.parts?.language?.handle ??
          recommendedLanguage;
      }

      const currentCountry = (window.Shopify?.country ?? "").toUpperCase();

      if (!detectedCountry || !currentCountry) return false;
      if (detectedCountry === currentCountry) return false;
      if (!this.isConnected) return false;

      this.#recommendedCountry = detectedCountry;
      this.#recommendedLanguage = recommendedLanguage;
      this.#detectedLocationName = this.#resolveLocationName(
        detectedCountry,
        detectedLocationName,
      );

      if (this.#countryExistsInSelect(detectedCountry)) {
        this.#selectCountry(detectedCountry);
      }

      this.#updateLocationTitle();
      return true;
    }

    #resolveLocationName(countryCode, preferredName = "") {
      const trimmedName = preferredName.trim();
      if (trimmedName) return trimmedName;

      const fromSelect = this.#countryNameFromSelect(countryCode);
      if (fromSelect) return fromSelect;

      try {
        const displayName = new Intl.DisplayNames(["en"], { type: "region" });
        return displayName.of(countryCode.toUpperCase()) || countryCode;
      } catch {
        return countryCode;
      }
    }

    #countryNameFromSelect(countryCode) {
      if (!this.#countrySelect || !countryCode) return "";

      const normalizedCode = countryCode.toUpperCase();

      for (const option of this.#countrySelect.options) {
        if (option.value.toUpperCase() !== normalizedCode) continue;

        return (
          option.dataset.countryName ||
          option.textContent.replace(/\s*\([^)]*\)\s*$/, "") ||
          ""
        ).trim();
      }

      return "";
    }

    #countryExistsInSelect(countryCode) {
      if (!this.#countrySelect) return false;

      const normalizedCode = countryCode.toUpperCase();

      for (const option of this.#countrySelect.options) {
        if (option.value.toUpperCase() === normalizedCode) {
          return true;
        }
      }

      return false;
    }

    #selectCountry(countryCode) {
      if (!this.#countrySelect) return;

      const normalizedCode = countryCode.toUpperCase();

      for (const option of this.#countrySelect.options) {
        if (option.value.toUpperCase() === normalizedCode) {
          this.#countrySelect.value = option.value;
          this.#updateFlagPreview();
          return;
        }
      }
    }

    #handleCountryChange = () => {
      this.#updateFlagPreview();
    };

    #updateLocationTitle() {
      const locationEl = this.#title?.querySelector(
        "[data-market-picker-location]",
      );
      const locationName =
        this.#detectedLocationName ||
        this.#countryNameFromSelect(this.#recommendedCountry);

      if (!locationEl || !locationName) return;

      locationEl.textContent = locationName;
    }

    #updateFlagPreview() {
      if (!this.#countrySelect || !this.#flagContainer) return;

      const selectedOption =
        this.#countrySelect.options[this.#countrySelect.selectedIndex];

      if (!selectedOption) return;

      const countryCode = selectedOption.value.toLowerCase();
      const flagImage = this.#flagContainer.querySelector("img");

      if (flagImage) {
        flagImage.src = `${FLAG_CDN_BASE}/${countryCode}.svg?width=${FLAG_WIDTH}`;
        flagImage.alt = selectedOption.textContent.trim();
      }
    }

    #handleFormSubmit = (event) => {
      /* Confirming counts as an answer — never re-prompt, even when the
         submitted market matches the current one (detected country outside
         the combined store lists). */
      this.#setDismissed();

      const redirectUrl = this.#selectedRedirectUrl();

      if (redirectUrl) {
        event.preventDefault();
        window.location.assign(redirectUrl);
        return;
      }

      this.#updateReturnTo();

      if (
        this.#localeInput &&
        this.#recommendedLanguage &&
        this.#countrySelect?.value.toUpperCase() ===
          this.#recommendedCountry.toUpperCase()
      ) {
        this.#localeInput.value = this.#recommendedLanguage;
      }
    };

    #selectedRedirectUrl() {
      const selectedOption =
        this.#countrySelect?.options[this.#countrySelect.selectedIndex];

      return selectedOption?.dataset.redirectUrl?.trim() || "";
    }

    #updateReturnTo() {
      const returnToInput = this.#form?.querySelector(
        'input[name="return_to"]',
      );
      const url = new URL(window.location.href);
      url.searchParams.delete("country");

      if (returnToInput) {
        const returnPath = `${url.pathname}${url.search}${url.hash}`;
        returnToInput.value = returnPath || "/";
      }

      if (window.location.search.includes("country=")) {
        window.history.replaceState({}, "", url.toString());
      }
    }

    #handleDismiss = (event) => {
      event.preventDefault();
      this.#setDismissed();
      this.#close();
    };

    #handleDialogClick = (event) => {
      if (event.target === this.#dialog) {
        this.#setDismissed();
        this.#close();
      }
    };

    #handleDialogCancel = (event) => {
      event.preventDefault();
      this.#setDismissed();
      this.#close();
    };

    #handleDialogClose = () => {
      this.#dialog?.classList.remove("market-picker-popup__dialog--active");
      document.documentElement.classList.remove("market-picker-open");
    };

    #open() {
      if (
        !this.isConnected ||
        !this.#dialog ||
        this.#dialog.open ||
        this.#isClosing
      ) {
        return;
      }

      this.#updateLocationTitle();
      this.#dialog.classList.remove("market-picker-popup__dialog--active");
      this.#dialog.showModal();
      document.documentElement.classList.add("market-picker-open");

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.#dialog.classList.add("market-picker-popup__dialog--active");
        return;
      }

      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#dialog?.classList.add("market-picker-popup__dialog--active");
      });
    }

    #close() {
      if (!this.#dialog?.open || this.#isClosing) return;

      this.#isClosing = true;

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.#finishClose();
        return;
      }

      this.#dialog.classList.remove("market-picker-popup__dialog--active");

      window.setTimeout(() => this.#finishClose(), CLOSE_ANIMATION_MS + 50);
    }

    #finishClose() {
      if (!this.#dialog?.open) {
        this.#isClosing = false;
        return;
      }

      this.#dialog.close();
      this.#isClosing = false;
    }
  }

  customElements.define(COMPONENT_NAME, MarketPickerPopup);
})();
